/***
 * File name: details.js
 *
 * Program Description:
 * This file contains all the meta configuration of the Data Breach Notification
 * Details tab in the Personal Data Breach Notification Form
 *
 * This file defines all the input fields needed for the Data Breach Notification
 * Details tab
 *
 * Includes the ff:
 * - Tab number indicator
 * - Label of the form
 * - optionQuery indicates the query params for fetching additional data from the
 * backend side of the system
 * - children includes all the input fields in the form
 * - formActions indicate all the action buttons to be included on top of the form
 * - Other configurations for the form
 *
 */
export default function (vm) {
  return {
    tab: 2,
    label: 'Data Breach Notification Details',
    optionQuery: {
    },
    optionName: [],
    children: [
      {
        is: 'v-row',
        children: [{ is: 'v-col' }]
      },
      // header
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h2',
                header: true,
                vText: 'Personal Data Breach Notification Details',
                vBind: {
                  style: 'color: #162b54'
                }
              }
            ]
          }
        ]
      },
      // 1st row
      {
        is: 'v-row',
        properties: {
          justify: 'center'
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'LookUpSelect',
                formKey: 'sector',
                vBind: {
                  label: 'Sector Name',
                  isDense: true,
                  id: 'sector',
                  name: 'sector',
                  allowFetch: vm.formItem.sector,
                  query: { type: 'sector' },
                  clearable: false,
                  itemText: 'value',
                  appendIcon: '',
                  canEdit: false,
                  textWrap: true,
                  outlined: !vm.readOnly
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'LookUpSelect',
                formKey: 'subsector',
                vBind: {
                  label: 'Sub Sector Name',
                  isDense: true,
                  id: 'sector',
                  name: 'sector',
                  allowFetch: vm.formItem.sector,
                  query: {
                    type: 'subsector',
                    jurisdiction: vm.formItem.sector
                  },
                  clearable: false,
                  itemText: 'value',
                  appendIcon: '',
                  canEdit: false,
                  textWrap: true,
                  outlined: !vm.readOnly
                }
              }
            ]
          }
        ]
      },
      // 2nd row
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: vm.status === 'Drafted' || vm.status === 'create' ? '' : 'notification',
                vBind: {
                  label: 'Notification Type',
                  dense: true,
                  id: 'notification',
                  items: ['Voluntary', 'Mandatory'],
                  appendIcon: '',
                  disabled: true,
                  outlined: true
                }
                // vOn: {
                //   input: () => { vm.$v.formItem.intro.$touch() },
                //   blur: () => { vm.$v.formItem.intro.$touch() }
                // }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: vm.status === 'Drafted' || vm.status === 'create' ? '' : 'timeliness',
                vBind: {
                  label: 'Timeliness',
                  dense: true,
                  id: 'timeliness',
                  items: ['Timely', 'Delayed'],
                  appendIcon: '',
                  disabled: true,
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'justified',
                vBind: {
                  class: vm.canUserEditField([{ userRole: 'evaluator', status: ['Submitted'] }, { userRole: 'reviewer', status: ['Evaluated'] }]) ? 'required-field' : '',
                  label: 'Justified, if Delayed',
                  dense: true,
                  id: 'justified',
                  items: ['Yes', 'No', 'Not applicable'],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.justified.$touch() },
                  blur: () => { vm.$v.formItem.justified.$touch() }
                }
              }
            ]
          }
        ]
      },
      // 3rd row
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'LookUpSelect',
                formKey: 'generalCause',
                vBind: {
                  label: 'General Cause',
                  class: 'required-field',
                  isDense: true,
                  id: 'generalCause',
                  name: 'causeOfIncident',
                  allowFetch: vm.formItem.generalCause,
                  query: { type: 'general' },
                  clearable: false,
                  itemText: 'value',
                  canEdit: !vm.readOnly && vm.canUserEditField([{ userRole: 'pic', status: ['create', 'Drafted'] }]),
                  textWrap: true,
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: () => {
                    vm.$set(vm.formItem, 'specificCause', undefined)
                    vm.$set(vm.formItem, 'specificCauseOthers', '')
                    vm.$v.formItem.specificCause.$reset()
                    vm.$v.formItem.specificCauseOthers.$reset()
                  }
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'LookUpSelect',
                formKey: 'specificCause',
                vBind: {
                  label: 'Specific Cause',
                  class: 'required-field',
                  isDense: true,
                  id: 'specificCause',
                  name: 'causeOfIncident',
                  allowFetch: vm.formItem.generalCause,
                  query: {
                    type: 'specific',
                    jurisdiction: vm.formItem.generalCause
                  },
                  clearable: false,
                  itemText: 'value',
                  additionalEntries: [
                    {
                      _id: 'Others',
                      value: 'Others'
                    }
                  ],
                  canEdit: vm.formItem.generalCause ? (!vm.readOnly && vm.canUserEditField([{ userRole: 'pic', status: ['create', 'Drafted'] }])) : false,
                  textWrap: true,
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: () => {
                    vm.$set(vm.formItem, 'specificCauseOthers', '')
                    vm.$v.formItem.specificCauseOthers.$reset()
                  }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.specificCause === 'Others',
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'specificCauseOthers',
                vBind: {
                  class: 'required-field cursor',
                  rows: '1',
                  label: 'Remarks',
                  dense: true,
                  id: 'specificCauseOthers',
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'pic', status: ['create', 'Drafted'] }]),
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'pic', status: ['create', 'Drafted'] }]),
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: () => { vm.$v.formItem.specificCauseOthers.$touch() },
                  blur: () => { vm.$v.formItem.specificCauseOthers.$touch() }
                }
              }
            ]
          }
        ]
      },
      // with request
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: 'With Request',
                vBind: {
                  style: 'color: black'
                },
                info: 'Refers to request for postponement of data subject notifications, alternative means, etc. For more information please refer to NPC Circular 16-03.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pl-4'
            },
            children: [
              {
                is: 'RadioButton',
                formKey: 'withRequest',
                vBind: {
                  value: vm.formItem.withRequest,
                  class: 'required-field pt-0 mt-0',
                  dense: true,
                  id: 'withRequest',
                  validationLabel: 'With Request',
                  isRow: true,
                  options: [
                    {
                      _id: 'Yes',
                      name: 'Yes'
                    },
                    {
                      _id: 'No',
                      name: 'No'
                    }
                  ],
                  canEdit: (!vm.readOnly && vm.canUserEditField([{ userRole: 'pic', status: ['create', 'Drafted'] }])) ||
                  (vm.canChange && vm.changeReqData.withRequest === 'No')
                },
                vOn: {
                  input: (val) => {
                    if (val === 'No') {
                      vm.$set(vm.formItem, 'typeOfRequest', [])
                      vm.$set(vm.formItem, 'requestExtension', '')
                      vm.$set(vm.formItem, 'extensionDays', '')
                      vm.$set(vm.formItem, 'typeOfRequestOther', '')
                      vm.$set(vm.formItem, 'justificationExemption', '')
                      vm.$set(vm.formItem, 'justificationPostponement', '')
                      vm.$set(vm.formItem, 'justificationAlternative', '')
                      vm.$set(vm.formItem, 'justificationOthers', '')
                      vm.$v.formItem.typeOfRequest && vm.$v.formItem.typeOfRequest.$reset()
                      vm.$v.formItem.requestExtension && vm.$v.formItem.requestExtension.$reset()
                      vm.$v.formItem.extensionDays && vm.$v.formItem.extensionDays.$reset()
                      vm.$v.formItem.typeOfRequestOther && vm.$v.formItem.typeOfRequestOther.$reset()
                      vm.$v.formItem.justificationExemption && vm.$v.formItem.justificationExemption.$reset()
                      vm.$v.formItem.justificationPostponement && vm.$v.formItem.justificationPostponement.$reset()
                      vm.$v.formItem.justificationAlternative && vm.$v.formItem.justificationAlternative.$reset()
                      vm.$v.formItem.justificationOthers && vm.$v.formItem.justificationOthers.$reset()

                      if (vm.formItem.endForDocumentSubmission) {
                        vm.$set(vm.formItem, 'extensionStart', '')
                        vm.$set(vm.formItem, 'extensionEnd', '')
                      }
                    }
                    vm.$v.formItem.withRequest && vm.$v.formItem.withRequest.$touch()
                  },
                  blur: () => { vm.$v.formItem.withRequest.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.withRequest === 'Yes',
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-select',
                formKey: 'typeOfRequest',
                info: 'Refers to the type of request that the PIC is asking NPC for approval ie, Request for Postponement of Data Subject notifications, alternative means, etc.',
                vBind: {
                  class: 'required-field',
                  label: 'Type of Request',
                  itemText: 'label',
                  items: vm.typeOfReqItems,
                  multiple: true,
                  dense: true,
                  id: 'typeOfRequest',
                  disabled: !vm.canUpdateRequest,
                  outlined: !vm.readOnly,
                  readonly: !vm.canUpdateRequest
                },
                vOn: {
                  input: (val) => {
                    if (!val.includes('Request for extension')) {
                      vm.$set(vm.formItem, 'requestExtension', '')
                      vm.$set(vm.formItem, 'extensionDays', '')
                      if (vm.formItem.endForDocumentSubmission) {
                        vm.$set(vm.formItem, 'extensionStart', '')
                        vm.$set(vm.formItem, 'extensionEnd', '')
                      }
                    }
                    if (!val.includes('Others')) {
                      vm.$set(vm.formItem, 'typeOfRequestOther', '')
                    }

                    if (vm.canChange && !val.includes(vm.changeReqData.availExtension)) {
                      vm.formItem.extensionDays = vm.changeReqData.extensionDays
                      vm.formItem.requestExtension = vm.changeReqData.requestExtension
                    } else if (vm.canChange && val.includes(vm.changeReqData.availExtension)) {
                      vm.formItem.extensionStart = vm.formItem.endForDocumentSubmission
                      vm.$set(vm.changeReqData, 'oldRequestExtension', vm.formItem.requestExtension || '')
                      vm.formItem.requestExtension = ''
                      vm.formItem.extensionDays = ''
                      if (!vm.formItem.extensionDays) vm.formItem.extensionEnd = ''
                    }
                    vm.$v.formItem.typeOfRequest.$touch()
                  },
                  blur: () => { vm.$v.formItem.typeOfRequest.$touch() },
                  focus: async () => {
                    const { waitForElement } = await import('@/utils/helpers.js')
                    const options = await waitForElement('.v-list-item--disabled')
                    for (const option of options) {
                      if (!option.innerText.includes('Request for extension')) continue
                      option.style.display = 'none'
                    }
                  }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Request for extension') && vm.formItem.withRequest === 'Yes' : false,
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-text-field',
                formKey: 'extensionDays',
                vBind: {
                  class: 'required-field ' + vm.transparent(!vm.canUpdateRequest ||
                  ((vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for extension')) &&
                  !(vm.canChange && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes(vm.changeReqData.availExtension)))),
                  label: 'Number of days requested for extension',
                  dense: true,
                  id: 'extensionDays',
                  disabled: !vm.canUpdateRequest ||
                  ((vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for extension')) &&
                  !(vm.canChange && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes(vm.changeReqData.availExtension))),
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: (val) => {
                    vm.$v.formItem.extensionDays.$touch()
                    if (val && vm.canChange && !vm.$v.formItem.extensionDays.$invalid) {
                      const date = vm.formItem.endForDocumentSubmission
                      const days = parseInt(vm.formItem.extensionDays)
                      vm.formItem.extensionEnd = vm.addDays(date, days)
                    } else if (vm.canChange) {
                      vm.formItem.extensionEnd = ''
                    }
                  },
                  blur: () => { vm.$v.formItem.extensionDays.$touch() }
                }
              }
            ]
          },
          {
            is: 'v-col',
            vShow: vm.formItem.endForDocumentSubmission && vm.formItem.endForDocumentSubmission !== '',
            vBind: {
              class: 'pb-0 pt-0 mt-0',
              cols: 4
            },
            children: [
              {
                is: 'DateComponent',
                formKey: 'extensionStart',
                vBind: {
                  value: vm.formItem.extensionStart,
                  dateClass: 'required-field',
                  label: 'Start Date',
                  dense: true,
                  noTitle: false,
                  wholeMonth: true,
                  id: 'extensionStart',
                  disabled: true,
                  outlined: !vm.readOnly
                }
              }
            ]
          },
          {
            is: 'v-col',
            vShow: vm.formItem.endForDocumentSubmission && vm.formItem.endForDocumentSubmission !== '',
            vBind: {
              class: 'pb-0 pt-0 mt-0',
              cols: 4
            },
            children: [
              {
                is: 'DateComponent',
                formKey: 'extensionEnd',
                vBind: {
                  value: vm.formItem.extensionEnd,
                  dateClass: 'required-field ' + vm.transparent(!vm.canUpdateRequest ||
                  ((vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for extension')) &&
                  !(vm.canChange && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes(vm.changeReqData.availExtension)))),
                  label: 'End Date',
                  dense: true,
                  noTitle: false,
                  wholeMonth: true,
                  id: 'extensionEnd',
                  disabled: true,
                  outlined: !vm.readOnly
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Request for extension') && vm.formItem.withRequest === 'Yes' : false,
        vBind: {
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'requestExtension',
                vBind: {
                  class: 'required-field cursor ' + vm.transparent(!vm.canUpdateRequest ||
                  ((vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for extension')) &&
                  !(vm.canChange && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes(vm.changeReqData.availExtension)))),
                  label: 'Explain the reason for the request for extension of time to submit the required documents and specify what documents are included therein',
                  validationLabel: 'Explanation',
                  id: 'requestExtension',
                  readonly: !vm.canUpdateRequest ||
                  ((vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for extension')) &&
                  !(vm.canChange && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes(vm.changeReqData.availExtension))),
                  disabled: !vm.canUpdateRequest ||
                  ((vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for extension')) &&
                  !(vm.canChange && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes(vm.changeReqData.availExtension))),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.requestExtension.$touch() },
                  blur: () => { vm.$v.formItem.requestExtension.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Request for exemption') && vm.formItem.withRequest === 'Yes' : false,
        vBind: {
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'justificationExemption',
                vBind: {
                  class: 'required-field cursor ' + vm.transparent(!vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for exemption'))),
                  label: 'Justification for exemption',
                  validationLabel: 'Explanation',
                  id: 'justificationExemption',
                  readonly: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for exemption')),
                  disabled: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for exemption')),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.justificationExemption.$touch() },
                  blur: () => { vm.$v.formItem.justificationExemption.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Request for postponement') && vm.formItem.withRequest === 'Yes' : false,
        vBind: {
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'justificationPostponement',
                vBind: {
                  class: 'required-field cursor ' + vm.transparent(!vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for postponement'))),
                  label: 'Justification for postponement',
                  validationLabel: 'Explanation',
                  id: 'justificationPostponement',
                  readonly: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for postponement')),
                  disabled: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for postponement')),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.justificationPostponement.$touch() },
                  blur: () => { vm.$v.formItem.justificationPostponement.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Request for alternative means of notification') && vm.formItem.withRequest === 'Yes' : false,
        vBind: {
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'justificationAlternative',
                vBind: {
                  class: 'required-field cursor ' + vm.transparent(!vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for alternative means of notification'))),
                  label: 'Justification for alternative means of notification',
                  validationLabel: 'Explanation',
                  id: 'justificationAlternative',
                  readonly: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for alternative means of notification')),
                  disabled: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Request for alternative means of notification')),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.justificationAlternative.$touch() },
                  blur: () => { vm.$v.formItem.justificationAlternative.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Others') && vm.formItem.withRequest === 'Yes' : false,
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-text-field',
                formKey: 'typeOfRequestOther',
                vBind: {
                  class: 'required-field',
                  label: 'Specify Others',
                  dense: true,
                  id: 'typeOfRequestOther',
                  disabled: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Others')),
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: () => { vm.$v.formItem.typeOfRequestOther.$touch() },
                  blur: () => { vm.$v.formItem.typeOfRequestOther.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.formItem.typeOfRequest ? vm.formItem.typeOfRequest.find(x => x === 'Others') && vm.formItem.withRequest === 'Yes' : false,
        vBind: {
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0 pt-0 mt-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'justificationOthers',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Justification for others',
                  validationLabel: 'Explanation',
                  id: 'justificationOthers',
                  readonly: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Others')),
                  disabled: !vm.canUpdateRequest ||
                  (vm.canChange && vm.changeReqData.typeOfRequest && vm.changeReqData.typeOfRequest.includes('Others')),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.justificationOthers.$touch() },
                  blur: () => { vm.$v.formItem.justificationOthers.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
        children: [
          {
            is: 'v-col',
            vShow: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-text-field',
                formKey: 'possibleViolation',
                vBind: {
                  label: 'Possible Violation of the DPA',
                  dense: true,
                  disabled: true,
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vShow: !(vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }])),
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'possibleViolation',
                vBind: {
                  class: 'required-field',
                  label: 'Possible Violation of the DPA',
                  dense: true,
                  items: [
                    'Yes',
                    'No',
                    'No information yet'
                  ],
                  id: 'possibleViolation',
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.possibleViolation.$touch() },
                  blur: () => { vm.$v.formItem.possibleViolation.$touch() }
                }
              }
            ]
          }
        ]
      },
      // 1a
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '1.a How breach occurred + DPS vulnerability',
                vBind: {
                  style: 'color: black'
                },
                info: 'Description of how the breach occurred and the vulnerability of the data processing system that allowed the breach.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'howBreachOccurredProvided',
                vBind: {
                  class: vm.formItem.howBreachOccurredProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'howBreachOccurredProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'howBreachOccurred',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  id: 'howBreachOccurred',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  dense: true,
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.howBreachOccurred.$touch() },
                  blur: () => { vm.$v.formItem.howBreachOccurred.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'howBreachOccurredRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'howBreachOccurredRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 1b
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '1.b Chronology',
                vBind: {
                  style: 'color: black'
                },
                info: 'Chronology of events.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'chronologyProvided',
                vBind: {
                  class: vm.formItem.chronologyProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'chronologyProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'chronology',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'chronology',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.chronology.$touch() },
                  blur: () => { vm.$v.formItem.chronology.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'chronologyRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'chronologyRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 1c
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '1.c Number of DS / Records',
                vBind: {
                  style: 'color: black'
                },
                info: 'Approximate number of data subjects or records involved.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'py-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'mt-3',
              cols: 3
            },
            children: [
              {
                is: 'v-text-field',
                formKey: 'numberOfDataSubjectsAffected',
                vBind: {
                  id: 'numberOfDataSubjectsAffected',
                  class: 'required-field',
                  type: 'number',
                  label: 'No. of Data Subjects affected',
                  min: '0',
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([
                    { userRole: 'evaluator', status: ['For Evaluation'] },
                    { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] },
                    { userRole: 'pic', status: ['create', 'Drafted'] }
                  ]),
                  outlined: true
                },
                vOn: {
                  input: (val) => {
                    vm.$v.formItem.numberOfDataSubjectsAffected.$touch()
                    vm.formItem.atLeastOneHundredIndividuals = (val >= 100)
                  },
                  blur: () => { vm.$v.formItem.numberOfDataSubjectsAffected.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pt-0',
              cols: 6
            },
            children: [
              {
                is: 'v-checkbox',
                formKey: 'atLeastOneHundredIndividuals',
                vBind: {
                  class: 'pa-0 ma-0',
                  label: 'At least one hundred (100) individuals',
                  disabled: true
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'numberOfDataSubjectsProvided',
                vBind: {
                  class: vm.formItem.numberOfDataSubjectsProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'numberOfDataSubjectsProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'numberOfDataSubjects',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'numberOfDataSubjects',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.numberOfDataSubjects.$touch() },
                  blur: () => { vm.$v.formItem.numberOfDataSubjects.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'numberOfDataSubjectsRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'numberOfDataSubjectsRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 1d
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '1.d Description / Nature',
                vBind: {
                  style: 'color: black'
                },
                info: 'An availability breach resulting from loss, accidental or unlawful destruction of personal data; Integrity breach resulting from alteration of personal data; and/or a confidentiality breach resulting from the unauthorized disclosure of or access to personal data.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'descriptionOrNatureProvided',
                vBind: {
                  class: vm.formItem.descriptionOrNatureProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'descriptionOrNatureProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'descriptionOrNature',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'descriptionOrNature',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.descriptionOrNature.$touch() },
                  blur: () => { vm.$v.formItem.descriptionOrNature.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'descriptionOrNatureRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'descriptionOrNatureRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 1e
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '1.e Likely Consequences',
                vBind: {
                  style: 'color: black'
                },
                info: 'Provide how the incident will affect both the Personal Information Controller and its data subjects.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'likelyConsequencesProvided',
                vBind: {
                  class: vm.formItem.likelyConsequencesProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'likelyConsequencesProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'likelyConsequences',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'likelyConsequences',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.likelyConsequences.$touch() },
                  blur: () => { vm.$v.formItem.likelyConsequences.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'likelyConsequencesRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'likelyConsequencesRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 1f
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '1.f DPO',
                vBind: {
                  style: 'color: black'
                },
                info: 'Name of the data protection officer or any other accountable person.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'dataProtectionOfficerProvided',
                vBind: {
                  class: vm.formItem.dataProtectionOfficerProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'dataProtectionOfficerProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'dataProtectionOfficer',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'dataProtectionOfficer',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.dataProtectionOfficer.$touch() },
                  blur: () => { vm.$v.formItem.dataProtectionOfficer.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'dataProtectionOfficerRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'dataProtectionOfficerRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 2a
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '2.a SPI',
                vBind: {
                  style: 'color: black'
                },
                info: `1. About an individual’s race, ethnic origin, marital status, age, color, and religious, philosophical or political affiliations;
                  2. About an individual’s health, education, genetic or sexual life of a person, or to any proceeding for any offense committed or alleged to have been committed by such person, the disposal of such proceedings, or the sentence of any court in such proceedings;
                  3. Issued by government agencies peculiar to an individual which includes, but not limited to, social security numbers, previous or current health records, licenses or its denials, suspension or revocation, and tax returns, and
                  4. Specifically established by an executive order or an act of Congress to be kept classified.
                `
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'sensitivePersonalInformationProvided',
                vBind: {
                  class: vm.formItem.sensitivePersonalInformationProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'sensitivePersonalInformationProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'sensitivePersonalInformation',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'sensitivePersonalInformation',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.sensitivePersonalInformation.$touch() },
                  blur: () => { vm.$v.formItem.sensitivePersonalInformation.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'sensitivePersonalInformationRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'sensitivePersonalInformationRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 2b
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '2.b Other info that may enable identity fraud',
                vBind: {
                  style: 'color: black'
                },
                info: 'Data about the financial or economic situation of the data subject; usernames, passwords and other login data; biometric data; copies of identification documents, licenses or unique identifiers like Philhealth, SSS, GSIS, TIN number; or other similar information, which may be made the basis of decisions concerning the data subject, including the grant of rights or benefits.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'identityFraudProvided',
                vBind: {
                  class: vm.formItem.identityFraudProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'identityFraudProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'identityFraud',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'identityFraud',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.identityFraud.$touch() },
                  blur: () => { vm.$v.formItem.identityFraud.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'identityFraudRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'identityFraudRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 3a
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '3.a Measures to address the breach',
                vBind: {
                  style: 'color: black'
                },
                info: 'Specific measures taken to address the incident including the results of the investigation conducted.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'measuresToAddressProvided',
                vBind: {
                  class: vm.formItem.measuresToAddressProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'measuresToAddressProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'measuresToAddress',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'measuresToAddress',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.measuresToAddress.$touch() },
                  blur: () => { vm.$v.formItem.measuresToAddress.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'measuresToAddressRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'measuresToAddressRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 3b
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '3.b Measures to secure/recover personal data?',
                vBind: {
                  style: 'color: black'
                },
                info: 'Actual measures taken to secure or recover personal data.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'measuresToSecureProvided',
                vBind: {
                  class: vm.formItem.measuresToSecureProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'measuresToSecureProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'measuresToSecure',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'measuresToSecure',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.measuresToSecure.$touch() },
                  blur: () => { vm.$v.formItem.measuresToSecure.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'measuresToSecureRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'measuresToSecureRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 3c
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '3.c Actions to mitigate harm?',
                vBind: {
                  style: 'color: black'
                },
                info: 'Actual measures taken to mitigate harm.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'actionsToMitigateHarmProvided',
                vBind: {
                  class: vm.formItem.actionsToMitigateHarmProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'actionsToMitigateHarmProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'actionsToMitigateHarm',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'actionsToMitigateHarm',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.actionsToMitigateHarm.$touch() },
                  blur: () => { vm.$v.formItem.actionsToMitigateHarm.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'actionsToMitigateHarmRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'actionsToMitigateHarmRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 3d
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '3.d Actions to inform data subjects?',
                vBind: {
                  style: 'color: black'
                },
                info: 'The actual manner of notification including any assistance extended to data subjects.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'actionsToInformDataSubjectsProvided',
                vBind: {
                  class: vm.formItem.actionsToInformDataSubjectsProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'actionsToInformDataSubjectsProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'actionsToInformDataSubjects',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'actionsToInformDataSubjects',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.actionsToInformDataSubjects.$touch() },
                  blur: () => { vm.$v.formItem.actionsToInformDataSubjects.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'actionsToInformDataSubjectsRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'actionsToInformDataSubjectsRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // 3e
      {
        is: 'v-row',
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: '3.e Measures to prevent recurrence of incidence',
                vBind: {
                  style: 'color: black'
                },
                info: 'Actual or proposed orientation materials addressing the vulnerability identified.'
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'measuresToPreventRecurrenceProvided',
                vBind: {
                  class: vm.formItem.measuresToPreventRecurrenceProvided === 'No information yet' ? 'required-field highlight' : 'required-field',
                  label: 'Sufficiently Provided',
                  dense: true,
                  id: 'measuresToPreventRecurrenceProvided',
                  items: [
                    'Yes',
                    'No',
                    'No information yet',
                    'Not applicable'
                  ],
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'measuresToPreventRecurrence',
                vBind: {
                  class: 'required-field cursor',
                  label: 'Provide Details',
                  dense: true,
                  id: 'measuresToPreventRecurrence',
                  rows: 3,
                  readonly: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  disabled: vm.readOnly || !(vm.status === 'create' || vm.status === 'Drafted'),
                  outlined: true
                },
                vOn: {
                  input: () => { vm.$v.formItem.measuresToPreventRecurrence.$touch() },
                  blur: () => { vm.$v.formItem.measuresToPreventRecurrence.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted',
            vBind: {
              class: 'pb-0',
              cols: 3
            },
            children: []
          },
          {
            is: 'v-col',
            vShow: vm.status !== 'create' && vm.status !== 'Drafted' && vm.status !== 'Submitted' && !vm.isPic,
            vBind: {
              class: 'pb-0',
              cols: 9
            },
            children: [
              {
                is: 'v-textarea',
                formKey: 'measuresToPreventRecurrenceRemarks',
                vBind: {
                  class: 'cursor',
                  label: 'Remarks',
                  id: 'measuresToPreventRecurrenceRemarks',
                  rows: 3,
                  readonly: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  dense: true,
                  disabled: vm.readOnly || !vm.canUserEditField([{ userRole: 'evaluator', status: ['For Evaluation'] }, { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] }]),
                  outlined: true
                }
              }
            ]
          }
        ]
      },
      // recordtype
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0 mb-1'
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'pb-0'
            },
            children: [
              {
                is: 'LookUpSelect',
                formKey: 'recordType',
                vBind: {
                  label: 'Record Type',
                  class: 'required-field mt-2',
                  isDense: true,
                  id: 'recordType',
                  name: 'recordType',
                  allowFetch: vm.formItem.recordType,
                  clearable: false,
                  itemText: 'value',
                  readonly: vm.readOnly || !vm.canUserEditField([
                    {
                      userRole: 'pic',
                      status: [
                        'create',
                        'Drafted'
                      ]
                    },
                    {
                      userRole: 'evaluator',
                      status: [
                        'For Evaluation',
                        'For Evaluation - PBNEF',
                        'For Evaluation - FBNER'
                      ]
                    },
                    {
                      userRole: 'reviewer',
                      status: [
                        'Initial Review',
                        'Final Review',
                        'Initial Review - PBNEF',
                        'Final Review - PBNEF',
                        'Initial Review - FBNER',
                        'Final Review - FBNER'
                      ]
                    }
                  ]),
                  canEdit: !vm.readOnly && vm.canUserEditField([
                    {
                      userRole: 'pic',
                      status: [
                        'create',
                        'Drafted'
                      ]
                    },
                    {
                      userRole: 'evaluator',
                      status: [
                        'For Evaluation',
                        'For Evaluation - PBNEF',
                        'For Evaluation - FBNER'
                      ]
                    },
                    {
                      userRole: 'reviewer',
                      status: [
                        'Initial Review',
                        'Final Review',
                        'Initial Review - PBNEF',
                        'Final Review - PBNEF',
                        'Initial Review - FBNER',
                        'Final Review - FBNER'
                      ]
                    }
                  ]),
                  outlined: !vm.readOnly,
                  tooltip: true,
                  tooltipInfo: 'Defines the type of record where involved personal data are recorded or stored at the time of the breach e.g.physical records can be printed materials, digital records in email may be email attachments, etc.'
                },
                vOn: {
                  input: () => { vm.$v.formItem.recordType.$touch() },
                  blur: () => { vm.$v.formItem.recordType.$touch() }
                }
              }
            ]
          }
        ]
      },
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            children: [
              {
                is: 'h3',
                header: true,
                vText: 'Data Subjects',
                vBind: {
                  style: 'color: black'
                }
              }
            ]
          }
        ]
      },
      // <v-autocomplete
      //     id="dataSubjects"
      //     v-model="dataSubjects"
      //     label="Data Subjects"
      //     :outlined="!$meta.readOnly"
      //     :disabled="$meta.readOnly"
      //     multiple
      //     deletable-chips
      //     small-chips
      //     :items="[
      //       'Own Employees',
      //       'Customers',
      //       'Personal data of vulnerable groups',
      //       'Others'
      //     ]"
      {
        is: 'v-row',
        vBind: {
          class: 'pt-0 mt-0'
        },
        children: [
          {
            is: 'v-col',
            vBind: {
              class: 'mt-3',
              cols: 6
            },
            children: [
              {
                is: 'v-autocomplete',
                formKey: 'dataSubjects',
                vBind: {
                  id: 'dataSubjects',
                  label: 'Data Subjects',
                  multiple: true,
                  deletableChips: true,
                  class: 'required-field',
                  smallChips: true,
                  disabled: vm.readOnly || !vm.canUserEditField([
                    { userRole: 'evaluator', status: ['For Evaluation'] },
                    { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] },
                    { userRole: 'pic', status: ['create', 'Drafted'] }
                  ]),
                  outlined: true,
                  items: ['Own Employees', 'Customers',
                    'Personal data of vulnerable groups',
                    'Others']
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              cols: 6,
              class: 'mt-3'
            },
            children: [
              {
                is: 'v-textarea',
                vShow: vm.formItem.dataSubjects && vm.formItem.dataSubjects.includes('Personal data of vulnerable groups'),
                formKey: 'specifyDataSubjectsVulnerable',
                vBind: {
                  class: 'required-field cursor',
                  rows: 1,
                  readonly: vm.readOnly || !vm.canUserEditField([
                    { userRole: 'evaluator', status: ['For Evaluation'] },
                    { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] },
                    { userRole: 'pic', status: ['create', 'Drafted'] }
                  ]),
                  label: 'Please specify (Personal data of vulnerable groups)',
                  disabled: vm.readOnly || !vm.canUserEditField([
                    { userRole: 'evaluator', status: ['For Evaluation'] },
                    { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] },
                    { userRole: 'pic', status: ['create', 'Drafted'] }
                  ]),
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: () => { vm.$v.formItem.specifyDataSubjectsVulnerable.$touch() },
                  blur: () => { vm.$v.formItem.specifyDataSubjectsVulnerable.$touch() }
                }
              }
            ]
          },
          {
            is: 'v-col',
            vBind: {
              cols: 6,
              class: 'pt-0'
            },
            children: [
              {
                is: 'v-textarea',
                vShow: vm.formItem.dataSubjects && vm.formItem.dataSubjects.includes('Others'),
                formKey: 'specifyDataSubjectsOthers',
                vBind: {
                  class: 'required-field cursor',
                  rows: 1,
                  readonly: vm.readOnly || !vm.canUserEditField([
                    { userRole: 'evaluator', status: ['For Evaluation'] },
                    { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] },
                    { userRole: 'pic', status: ['create', 'Drafted'] }
                  ]),
                  label: 'Please specify (Others)',
                  disabled: vm.readOnly || !vm.canUserEditField([
                    { userRole: 'evaluator', status: ['For Evaluation'] },
                    { userRole: 'reviewer', status: ['Initial Review', 'Final Review'] },
                    { userRole: 'pic', status: ['create', 'Drafted'] }
                  ]),
                  outlined: !vm.readOnly
                },
                vOn: {
                  input: () => { vm.$v.formItem.specifyDataSubjectsOthers.$touch() },
                  blur: () => { vm.$v.formItem.specifyDataSubjectsOthers.$touch() }
                }
              }
            ]
          }
        ]
      }
    ],
    formActions: [],
    canEditNotifType:
    vm.readOnly ||
    !vm.canUserEditField([
      {
        userRole: 'pic',
        status: [
          'create',
          'Drafted'
        ]
      },
      {
        userRole: 'evaluator',
        status: [
          'For Evaluation',
          'For Evaluation - PBNEF',
          'For Evaluation - FBNER'
        ]
      },
      {
        userRole: 'reviewer',
        status: [
          'Initial Review',
          'Final Review',
          'Initial Review - PBNEF',
          'Final Review - PBNEF',
          'Initial Review - FBNER',
          'Final Review - FBNER'
        ]
      }
    ])
  }
}
